import { apiRoutes } from '@/app/apiConfig/apiRoutes';
import { Licensee, LicenseesWithSalesTeamIds } from '@/app/shared-components/Types/Licensees';
import { Paged } from '../shared-components/Types/Paged';
import { apiSlice } from './apiSlice';
import { WorkingTypeResponse } from 'app/shared-components/Types/WorkingDays';

export interface MessagingRuleOptOut {
  triggerId: number;
  channelTypeId: number;
}

export interface LicenseeMessagingRuleOptOuts {
  licenseeId: number;
  licenseeName: string;
  optOuts: MessagingRuleOptOut[];
}

export const licenseesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getLicensees: builder.query<Paged<Licensee>, void>({
      query: () => {
        return {
          url: apiRoutes.licensees,
          method: 'GET',
        };
      },
      providesTags: ['Licensees'],
    }),
    getLicenseesByNameParameter: builder.query<Paged<Licensee>, string>({
      query: (search: string) => {
        const filterSign = '@=*';
        const parameter = 'name';
        const baseUrl = new URL(import.meta.env.VITE_API_URL);
        baseUrl.searchParams.append('filters', `${parameter}${filterSign}${search}`);
        const url = `${apiRoutes.licensees}${baseUrl.search}`;
        return {
          url,
          method: 'GET',
        };
      },
    }),
    getLicenseesByIds: builder.query<Paged<Licensee>, string>({
      query: (search: string) => {
        const url = `${apiRoutes.licensees}?filters=id==${search}`;
        return {
          url,
          method: 'GET',
        };
      },
    }),
    getLicenseesWorkingDaysConfiguration: builder.query<
      WorkingTypeResponse,
      { year: number; month: number }
    >({
      query: ({ year, month }) => {
        const url = `${apiRoutes.licensees}/config/working-days/${year}/${month}`;
        return {
          url,
          method: 'GET',
        };
      },
    }),
    getLicenseesReportsSalesTeamIds: builder.query<LicenseesWithSalesTeamIds[], void>({
      query: () => {
        return {
          url: `${apiRoutes.licensees}/reports`,
          method: 'GET',
        };
      },
    }),
    getMessagingRuleOptOuts: builder.query<MessagingRuleOptOut[], number>({
      query: (licenseeId) => ({
        url: `${apiRoutes.licensees}/${licenseeId}/messaging-rule-opt-outs`,
        method: 'GET',
      }),
      providesTags: ['MessagingRuleOptOuts'],
    }),
    getAllLicenseesMessagingRuleOptOuts: builder.query<LicenseeMessagingRuleOptOuts[], void>({
      query: () => ({
        url: `${apiRoutes.licensees}/messaging-rule-opt-outs`,
        method: 'GET',
      }),
      providesTags: ['MessagingRuleOptOuts'],
    }),
    updateMessagingRuleOptOut: builder.mutation<
      void,
      { licenseeId: number; triggerId: number; channelTypeId: number; isOptedOut: boolean }
    >({
      query: ({ licenseeId, triggerId, channelTypeId, isOptedOut }) => ({
        url: `${apiRoutes.licensees}/${licenseeId}/messaging-rule-opt-out`,
        method: 'PUT',
        data: { triggerId, channelTypeId, isOptedOut },
      }),
      invalidatesTags: ['MessagingRuleOptOuts'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetLicenseesQuery,
  useLazyGetLicenseesByNameParameterQuery,
  useLazyGetLicenseesByIdsQuery,
  useGetLicenseesReportsSalesTeamIdsQuery,
  useGetLicenseesWorkingDaysConfigurationQuery,
  useGetMessagingRuleOptOutsQuery,
  useGetAllLicenseesMessagingRuleOptOutsQuery,
  useUpdateMessagingRuleOptOutMutation,
} = licenseesApiSlice;
